/**
 * The small things over the field: the play-call toast (at the snap, gone after `--t-toast`, never
 * persistent; a call such as SACK uses it too) and the speed pill (one button, 1x → 2x → skip).
 */

export class Toast {
  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private readonly el: HTMLElement,
    private readonly ms: () => number,
  ) {}

  show(text: string): void {
    this.el.textContent = text;
    this.el.hidden = false;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.hide(), this.ms());
  }

  hide(): void {
    clearTimeout(this.timer);
    this.el.hidden = true;
  }
}

export type Speed = 1 | 2 | 'skip';
const NEXT: Record<string, Speed> = { '1': 2, '2': 'skip', skip: 1 };
const LABEL: Record<string, string> = { '1': '1x', '2': '2x', skip: 'Skip' };

export class SpeedPill {
  speed: Speed = 1;

  constructor(
    private readonly button: HTMLButtonElement,
    onChange: (s: Speed) => void,
  ) {
    button.addEventListener('click', () => {
      this.set(NEXT[String(this.speed)] as Speed);
      onChange(this.speed);
    });
    this.set(1);
  }

  set(s: Speed): void {
    this.speed = s;
    this.button.textContent = LABEL[String(s)] ?? '1x';
    this.button.setAttribute('aria-label', `Playback speed ${LABEL[String(s)]}, tap to change`);
  }

  /** Real-time multiplier for the field and the text box; skip plays as fast as it can. */
  get factor(): number {
    return this.speed === 'skip' ? Infinity : this.speed;
  }
}
